import { useRef, useState } from 'react'
import { Input } from './Input'
import { Button } from './Button'
import { Alert } from './Alert'

// Same share-a-link UX as the "Invitations" tab in App.tsx (generate link →
// copy input + button → editable subject/message → mailto: send) — reused
// here for matter-upload-links and signer links, which follow the exact
// same "an unguessable link is the credential" trust model as
// client-invites (see backend/src/index.ts / backend/src/esign.ts).
interface ShareLinkPanelProps {
  link: string
  defaultSubject: string
  defaultMessage: string
  recipientEmail?: string
}

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())

export const ShareLinkPanel: React.FC<ShareLinkPanelProps> = ({ link, defaultSubject, defaultMessage, recipientEmail }) => {
  const [copied, setCopied] = useState(false)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [email, setEmail] = useState(recipientEmail || '')
  const [subject, setSubject] = useState(defaultSubject)
  const [message, setMessage] = useState(defaultMessage)
  const linkInputRef = useRef<HTMLInputElement>(null)

  const handleCopy = async () => {
    setCopyError(null)

    // navigator.clipboard only works on HTTPS or literally "localhost" —
    // fall back to the classic select+execCommand approach otherwise (same
    // as App.tsx's client-invite copy handler).
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(link)
        setCopied(true)
        return
      } catch {
        // fall through to legacy fallback
      }
    }

    const input = linkInputRef.current
    if (input) {
      input.focus()
      input.select()
      try {
        if (document.execCommand('copy')) {
          setCopied(true)
          return
        }
      } catch {
        // fall through
      }
    }

    setCopyError('Could not copy link — please copy it manually')
  }

  const emailValid = isValidEmail(email)

  // mailto: hands off to whatever the browser/OS has registered as the
  // default mail app (Outlook, Mail.app, a webmail handler, ...) — there's
  // no way to target a specific client from a web page beyond that.
  const handleSend = () => {
    if (!emailValid) return
    window.location.href = `mailto:${email.trim()}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`
  }

  return (
    <div>
      {copyError && <Alert type="error" className="mb-3">{copyError}</Alert>}

      <div className="flex flex-wrap gap-2 items-center">
        <input
          ref={linkInputRef}
          readOnly
          value={link}
          onFocus={(e) => e.currentTarget.select()}
          className="flex-1 min-w-[240px] px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white"
        />
        <Button type="button" size="sm" variant="secondary" onClick={handleCopy}>
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">Send Link</h4>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent mb-2"
        />
        <Input label="Email subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mb-2" />
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent"
          />
        </div>
        <Button type="button" size="sm" onClick={handleSend} disabled={!emailValid}>
          Send Email
        </Button>
        <p className="text-xs text-gray-500 mt-2">
          Enter a valid email to enable sending. This opens your default email app with the message pre-filled.
        </p>
      </div>
    </div>
  )
}
